var { initIframePrint } = require('./shared/iframe-print');

/**
 * Packing List - Client-Side interactions
 * Handles toggle/add/remove with localStorage for custom items only (not checked state)
 */

(function() {
    'use strict';

    const STORAGE_KEY = 'packing-list-custom-items';
    
    /**
     * Initialize all handlers when DOM is ready
     */
    function init() {
        restoreScrollPosition();
        
        // Save scroll position and sync state before form submissions
        const form = document.getElementById('packingForm');
        if (form) {
            form.addEventListener('submit', function(e) {
                const handler = getPageHandler(e.submitter);
                
                // Sync current client-side state to hidden fields before submission
                syncStateToHiddenFields();
                
                // Clear localStorage when generating a new list
                if (handler === 'GenerateList') {
                    localStorage.removeItem(STORAGE_KEY);
                }
                
                // Clear scroll position when confirming reset to scroll to top
                if (handler === 'ConfirmReset') {
                    try {
                        sessionStorage.removeItem('packingListScrollPos');
                    } catch (ex) {
                        // Ignore errors
                    }
                    return; // Don't call saveScrollPosition
                }
                
                saveScrollPosition();
            });
        }
        
        setupToggleHandlers();
        setupRemoveHandlers();
        setupAddHandlers();
        setupEditHandlers();
        setupActivityButtons();
        setupResetConfirmation();
        setupPrintSync();

        // Wire the iframe print flow for the hidden print form
        initIframePrint({
            formId: 'plPrintForm',
            iframeId: 'plPrintFrame',
            buttonId: 'plPrintBtn',
            readyLabel: '<i class="bi bi-printer me-2" aria-hidden="true"></i>Print List',
        });
    }

    /**
     * Save current scroll position to sessionStorage before form submission
     * Skips saving if the confirmation modal is visible to preserve the original position
     */
    function saveScrollPosition() {
        const modalOverlay = document.querySelector('.clear-confirmation-overlay.is-visible');
        if (modalOverlay) {
            return;
        }

        try {
            sessionStorage.setItem('packingListScrollPos', window.pageYOffset.toString());
        } catch (e) {
            // Ignore storage errors
        }
    }

    /**
     * Restore scroll position from sessionStorage after page load
     */
    function restoreScrollPosition() {
        try {
            const scrollPos = sessionStorage.getItem('packingListScrollPos');
            if (scrollPos) {
                window.scrollTo(0, parseInt(scrollPos, 10));
                
                const modalOverlay = document.querySelector('.clear-confirmation-overlay.is-visible');
                if (!modalOverlay) {
                    sessionStorage.removeItem('packingListScrollPos');
                }
            }
        } catch (e) {
            // Ignore storage errors
        }
    }

    /**
     * Sync current client-side state (checked items, custom items) to hidden fields
     * This ensures form submissions preserve the current state
     */
    function syncStateToHiddenFields() {
        // Update hidden fields with current checked state and custom items
        document.querySelectorAll('.packing-category').forEach((categoryEl, catIndex) => {
            const items = categoryEl.querySelectorAll('.packing-item');
            items.forEach((itemEl, itemIndex) => {
                const isChecked = itemEl.classList.contains('checked');
                const text = itemEl.querySelector('.item-text').textContent.trim();
                
                // Update or create hidden fields
                updateHiddenField(`Categories[${catIndex}].Items[${itemIndex}].Text`, text);
                updateHiddenField(`Categories[${catIndex}].Items[${itemIndex}].IsPacked`, isChecked.toString().toLowerCase());
            });
        });
    }

    /**
     * Update or create a hidden field with the given name and value
     */
    function updateHiddenField(name, value) {
        var form = document.getElementById('packingForm');
        if (!form) return;
        
        var field = form.querySelector(`input[type="hidden"][name="${name}"]`);
        if (field) {
            field.value = value;
        } else {
            // Create new hidden field if it doesn't exist
            field = document.createElement('input');
            field.type = 'hidden';
            field.name = name;
            field.value = value;
            form.appendChild(field);
        }
    }

    /**
     * Setup reset confirmation modal controls
     */
    function setupResetConfirmation() {
        const resetButton = document.getElementById('packingListReset');
        const overlay = document.getElementById('resetConfirmationOverlay');
        if (!resetButton || !overlay) {
            return;
        }

        const closeButtons = overlay.querySelectorAll('[data-reset-confirmation="close"]');

        resetButton.addEventListener('click', function () {
            openResetConfirmation(overlay);
        });

        closeButtons.forEach(function (button) {
            button.addEventListener('click', function () {
                closeResetConfirmation(overlay);
            });
        });
    }

    /**
     * Open the reset confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function openResetConfirmation(overlay) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = scrollbarWidth + 'px';
        }
        overlay.classList.add('is-visible');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('clear-confirmation-open');
    }

    /**
     * Close the reset confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function closeResetConfirmation(overlay) {
        overlay.classList.remove('is-visible');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('clear-confirmation-open');
        document.body.style.paddingRight = '';
    }

    /**
     * Sync the current DOM state into the print form's InputJson hidden field
     * before the print form submits, so added/removed/renamed items appear on print.
     */
    function setupPrintSync() {
        var printForm = document.getElementById('plPrintForm');
        if (!printForm) return;

        printForm.addEventListener('submit', function() {
            var inputField = printForm.querySelector('input[name="InputJson"]');
            if (!inputField) return;

            var mainForm = document.getElementById('packingForm');
            var tripType = mainForm ? (mainForm.querySelector('[name="TripType"]') || {}).value || 'beach' : 'beach';
            var duration = mainForm ? (mainForm.querySelector('[name="Duration"]') || {}).value || 'weekend' : 'weekend';
            var climate = mainForm ? (mainForm.querySelector('[name="Climate"]') || {}).value || 'warm' : 'warm';
            var destination = mainForm ? (mainForm.querySelector('[name="Destination"]') || {}).value || '' : '';

            var categories = [];
            document.querySelectorAll('.packing-category').forEach(function(categoryEl) {
                var headerEl = categoryEl.querySelector('.category-header h5');
                var iconEl = headerEl ? headerEl.querySelector('i') : null;

                var name = '';
                if (headerEl) {
                    // Get text content excluding the count badge
                    var clone = headerEl.cloneNode(true);
                    var badge = clone.querySelector('.category-count');
                    if (badge) badge.remove();
                    var iconClone = clone.querySelector('i');
                    if (iconClone) iconClone.remove();
                    name = clone.textContent.trim();
                }

                var icon = 'bi-box';
                if (iconEl) {
                    var classes = iconEl.className.split(' ');
                    for (var c = 0; c < classes.length; c++) {
                        if (classes[c].indexOf('bi-') === 0 && classes[c] !== 'bi') {
                            icon = classes[c];
                            break;
                        }
                    }
                }

                var items = [];
                categoryEl.querySelectorAll('.packing-item').forEach(function(itemEl) {
                    var textEl = itemEl.querySelector('.item-text');
                    var text = textEl ? textEl.textContent.trim() : '';
                    var isPacked = itemEl.classList.contains('checked');
                    if (text) {
                        items.push({ text: text, isPacked: isPacked });
                    }
                });

                categories.push({ name: name, icon: icon, items: items });
            });

            var data = {
                tripType: tripType,
                duration: duration,
                climate: climate,
                destination: destination,
                categories: categories
            };

            inputField.value = JSON.stringify(data);
        });
    }

    /**
     * Setup toggle item handlers - Pure client-side, ephemeral (not persisted)
     */
    function setupToggleHandlers() {
        document.addEventListener('click', function(e) {
            const toggleBtn = e.target.closest('[data-action="toggle"]');
            if (!toggleBtn) return;
            
            // Update UI instantly
            const packingItem = toggleBtn.closest('.packing-item');
            if (packingItem) {
                const isChecked = packingItem.classList.contains('checked');
                packingItem.classList.toggle('checked', !isChecked);
                
                const icon = toggleBtn.querySelector('i');
                if (icon) {
                    icon.className = isChecked ? 'bi bi-square' : 'bi bi-check-square-fill';
                }
                
                // Update progress
                updateProgress();
            }
        });
    }

    /**
     * Setup remove item handlers - Pure client-side
     */
    function setupRemoveHandlers() {
        document.addEventListener('click', function(e) {
            const removeBtn = e.target.closest('[data-action="remove"]');
            if (!removeBtn) return;
            
            // Fade out and remove from DOM
            const packingItem = removeBtn.closest('.packing-item');
            const colDiv = packingItem.closest('.col-md-6');
            if (colDiv) {
                colDiv.style.opacity = '0';
                colDiv.style.transition = 'opacity 0.2s';
                setTimeout(() => {
                    colDiv.remove();
                    updateDataAttributes();
                    updateProgress();
                }, 200);
            }
        });
    }

    /**
     * Setup add item handlers - Pure client-side
     */
    function setupAddHandlers() {
        document.addEventListener('click', function(e) {
            const addBtn = e.target.closest('[data-action="add"]');
            if (!addBtn) return;
            
            const categoryIndex = parseInt(addBtn.getAttribute('data-category'));
            const inputField = document.getElementById('newItemText-' + categoryIndex);
            const newItemText = inputField ? inputField.value.trim() : '';
            
            if (!newItemText) return;
            
            // Get category element
            const categoryEl = addBtn.closest('.packing-category');
            const itemsRow = categoryEl.querySelector('.category-items .row');
            
            // Count current items to get new index
            const currentItems = itemsRow.querySelectorAll('.packing-item');
            const newItemIndex = currentItems.length;
            
            // Create new item
            const colDiv = document.createElement('div');
            colDiv.className = 'col-md-6';
            colDiv.innerHTML = `
                <div class="packing-item">
                    <button type="button" class="item-toggle" data-action="toggle" data-category="${categoryIndex}" data-item="${newItemIndex}">
                        <i class="bi bi-square"></i>
                    </button>
                    <span class="item-text">${escapeHtml(newItemText)}</span>
                    <button type="button" class="btn-edit" title="Rename" data-action="edit" data-category="${categoryIndex}" data-item="${newItemIndex}">
                        <i class="bi bi-pencil"></i>
                    </button>
                    <button type="button" class="btn-remove" title="Remove" data-action="remove" data-category="${categoryIndex}" data-item="${newItemIndex}">
                        <i class="bi bi-x"></i>
                    </button>
                </div>
            `;
            
            itemsRow.appendChild(colDiv);
            
            // Clear input
            inputField.value = '';
            
            // Update progress and category count
            updateProgress();
        });

        // Allow Enter key to add items
        document.addEventListener('keypress', function(e) {
            if (e.key !== 'Enter') return;

            const input = e.target.closest('[id^="newItemText-"]');
            if (!input) return;

            e.preventDefault();
            const categoryIndex = input.id.replace('newItemText-', '');
            const addBtn = document.querySelector(`[data-action="add"][data-category="${categoryIndex}"]`);
            if (addBtn) addBtn.click();
        });
    }

    /**
     * Setup edit/rename item handlers - Inline editing with Enter/Escape/blur support
     */
    function setupEditHandlers() {
        document.addEventListener('click', function(e) {
            const editBtn = e.target.closest('[data-action="edit"]');
            if (!editBtn) return;

            const packingItem = editBtn.closest('.packing-item');
            if (!packingItem) return;

            const textSpan = packingItem.querySelector('.item-text');
            if (!textSpan || packingItem.querySelector('.item-edit-input')) return;

            const currentText = textSpan.textContent.trim();
            const input = document.createElement('input');
            input.type = 'text';
            input.className = 'item-edit-input';
            input.value = currentText;
            input.maxLength = 50;

            textSpan.replaceWith(input);
            input.focus();
            input.select();

            /** @param {boolean} save */
            function finishEdit(save) {
                const newText = input.value.trim();
                const span = document.createElement('span');
                span.className = 'item-text';
                span.textContent = (save && newText) ? newText : currentText;
                input.replaceWith(span);
            }

            input.addEventListener('keydown', function(e) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    finishEdit(true);
                } else if (e.key === 'Escape') {
                    finishEdit(false);
                }
            });

            input.addEventListener('blur', function() {
                if (packingItem.contains(input)) {
                    finishEdit(true);
                }
            });
        });
    }

    /**
     * Update progress bar based on current DOM state
     */
    function updateProgress() {
        let totalItems = 0;
        let packedItems = 0;
        
        // Count all items in the DOM
        document.querySelectorAll('.packing-item').forEach(item => {
            totalItems++;
            if (item.classList.contains('checked')) {
                packedItems++;
            }
        });
        
        const percentage = totalItems > 0 ? Math.round((packedItems / totalItems) * 100) : 0;
        
        // Update progress bar
        const progressBar = document.querySelector('.progress-bar');
        const progressLabel = document.querySelector('.progress-label');
        const progressSummary = document.querySelector('.progress-summary');
        
        if (progressBar) {
            progressBar.style.width = percentage + '%';
        }
        if (progressLabel) {
            progressLabel.textContent = percentage + '% Packed';
            if (percentage >= 50) {
                progressLabel.classList.add('light-text');
            } else {
                progressLabel.classList.remove('light-text');
            }
        }
        
        // Update the items count text (can be positioned anywhere)
        const itemsCountText = progressSummary ? progressSummary.querySelector('.items-count-text') : null;
        if (itemsCountText) {
            itemsCountText.innerHTML = `<strong>${packedItems}</strong> of <strong>${totalItems}</strong> items packed`;
        }
        
        // Update each category count
        document.querySelectorAll('.packing-category').forEach(categoryEl => {
            const items = categoryEl.querySelectorAll('.packing-item');
            const checkedInCategory = categoryEl.querySelectorAll('.packing-item.checked').length;
            const totalInCategory = items.length;
            
            const countEl = categoryEl.querySelector('.category-count');
            if (countEl) {
                countEl.textContent = `${checkedInCategory}/${totalInCategory}`;
            }
        });
    }

    /**
     * Setup activity button handlers - Toggle active state when clicked
     */
    function setupActivityButtons() {
        var activityButtons = document.querySelectorAll('.activity-btn');
        activityButtons.forEach(function(btn) {
            var checkbox = btn.querySelector('input[type="checkbox"]');
            if (checkbox) {
                checkbox.addEventListener('change', function() {
                    if (this.checked) {
                        btn.classList.add('active');
                    } else {
                        btn.classList.remove('active');
                    }
                });
            }
        });
    }

    /**
     * Update data-category and data-item attributes after DOM changes
     */
    function updateDataAttributes() {
        document.querySelectorAll('.packing-category').forEach((categoryEl, catIndex) => {
            categoryEl.querySelectorAll('.packing-item').forEach((itemEl, itemIndex) => {
                const toggleBtn = itemEl.querySelector('[data-action="toggle"]');
                const removeBtn = itemEl.querySelector('[data-action="remove"]');
                
                if (toggleBtn) {
                    toggleBtn.setAttribute('data-category', catIndex);
                    toggleBtn.setAttribute('data-item', itemIndex);
                }
                if (removeBtn) {
                    removeBtn.setAttribute('data-category', catIndex);
                    removeBtn.setAttribute('data-item', itemIndex);
                }
            });
        });
    }

    /**
     * Escape HTML special characters
     */
    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Get the page handler name from a button's formaction attribute
     * asp-page-handler="Foo" renders as formaction="...?handler=Foo"
     */
    function getPageHandler(button) {
        if (!button) return null;
        var formaction = button.getAttribute('formaction');
        if (formaction) {
            var match = formaction.match(/[?&]handler=([^&]+)/i);
            if (match) {
                return match[1];
            }
        }
        return null;
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();

